import React, { useState, useEffect } from "react";
import { Ripple } from "primereact/ripple";
import { Dialog } from 'primereact/dialog';
import { Button } from "primereact/button";
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { Nullable } from "primereact/ts-helpers";
import TextInput from "../../../../../Components/TextInput";
import FileInput from "../../../../../Components/FileInput";
import DatePicker from "../../../../../Components/DatePicker";
import { CompletionCertificates } from "../../../../../Controllers/Types/Project/DefaultService/CompletionCertificateInterface";
import { completionCertificateCreateAPI, completionCertificateUpdateAPI } from "../../../../../Api/Project/DefaultServices/CompletionCertificateApi";
import { checkFileOthers, checkText } from "../../../../../Utils/Validator";
import { formatDate, parseStringToDate } from "../../../../../Utils/getDate";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../Controllers/Store";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";

interface CompletionCertificateModalProps {
    isOpen: boolean;
    dataState: 'Add' | 'Edit';
    onClose: () => void;
    onFetchData: () => void;
    toast: React.RefObject<Toast>;
    serviceId: number;
    selectedComCerData?: CompletionCertificates
}

const CompletionCertificateModal: React.FC<CompletionCertificateModalProps> = ({
    isOpen,
    dataState,
    onClose,
    onFetchData,
    toast,
    serviceId,
    selectedComCerData
}) => {
    const today = new Date();
    const [loading, setLoading] = useState<boolean>(false);
    const [statuses] = useState<string[]>(['Received', 'In Progress', 'Pending']);

    /********************************* Form variables *************************************/
    const [certificateTitle, setCertificateTitle] = useState<string>('');
    const [appliedDate, setAppliedDate] = useState<Nullable<Date>>(null);
    const [status, setStatus] = useState<string>('');

    const [file, setFile] = useState<File | null>(null);
    const [fileName, setFileName] = useState<string>('');

    /* Error message */
    const [certificateTitleError, setCertificateTitleError] = useState<string>('');
    const [appliedDateError, setAppliedDateError] = useState<string>('');
    const [statusError, setStatusError] = useState<string>('');
    const [fileError, setFileError] = useState<string>('');
    /* ********************************************************************************** */

    const [updateHasChanges, setUpdateHasChanges] = useState<boolean>(false);

    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) => hasPermission(state, screen, permission));
    };


    const isPermission_change_progress = useCanEdit('Completion Certificate', 'change_progress');

    useEffect(() => {
        setDataValue();
    }, [dataState, selectedComCerData]);

    function setDataValue() {
        if (dataState === "Add") {
            handleClearFields();
        } else {
            setCertificateTitle(selectedComCerData?.certificate_title!);
            setAppliedDate(parseStringToDate(selectedComCerData?.applied_date! || ''));
            setStatus(selectedComCerData?.status!);
            setFile(null);
            setFileName('');
        }
    }

    const handleClearFields = () => {
        setCertificateTitle('');
        setAppliedDate(null);
        setStatus('');
        setFile(null);
        setFileName('');
    }

    const handleClearErrors = () => {
        setCertificateTitleError('');
        setAppliedDateError('');
        setStatusError('');
        setFileError('');
    }

    /* Clear errors */
    useEffect(() => {
        checkText(certificateTitle, false, setCertificateTitleError);
    }, [certificateTitle]);

    useEffect(() => {
        checkText(formatDate(appliedDate!), false, setAppliedDateError);
    }, [appliedDate]);

    useEffect(() => {
        checkText(status!, false, setStatusError);
    }, [status]);

    useEffect(() => {
        checkFileOthers(file!, false, setFileError);
    }, [file]);
    /*  */

    function checkCredentials(appliedDate: string): boolean {
        const isCertificateTitleValid = checkText(certificateTitle, true, setCertificateTitleError);
        const isAppliedDateValid = checkText(appliedDate, false, setAppliedDateError);
        const isStatusValid = checkText(status!, true, setStatusError);
        const isFileValid = checkFileOthers(file!, false, setFileError);

        return isCertificateTitleValid && isAppliedDateValid && isStatusValid && isFileValid;
    }

    /* ********************************************* Create ************************************** */
    const handleCreateDetails = async (e: React.FormEvent) => {
        e.preventDefault();

        const appliedDateDateOnly = formatDate(appliedDate || null);
        if (checkCredentials(appliedDateDateOnly)) {
            setLoading(true);
            const data = await completionCertificateCreateAPI(
                Number(serviceId),
                certificateTitle,
                appliedDateDateOnly!,
                status!,
                file!
            );
            setLoading(false);

            if (data.statusCode === 200) {
                onFetchData();
                handleCloseModal();

                if (toast.current) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Success",
                        detail: `Certificate details added successfully.`,
                        life: 3000,
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error creating certificate.',
                        life: 3000
                    });
                }
            }
        }
    }
    /* ********************************************* Create ************************************** */


    /* ********************************************* Update ************************************** */
    useEffect(() => {
        if (selectedComCerData) {
            if (certificateTitle !== selectedComCerData?.certificate_title) {
                setUpdateHasChanges(true);
            } else if (formatDate(appliedDate!) !== selectedComCerData?.applied_date) {
                setUpdateHasChanges(true);
            } else if (status !== selectedComCerData?.status) {
                setUpdateHasChanges(true);
            } else if (file !== null) {
                setUpdateHasChanges(true);
            } else {
                setUpdateHasChanges(false);
            }
        }
    }, [selectedComCerData, certificateTitle, appliedDate, status, file])

    const handleUpdateDetails = async (e: React.FormEvent) => {
        e.preventDefault();

        const appliedDateDateOnly = formatDate(appliedDate || null);
        if (checkCredentials(appliedDateDateOnly)) {
            setLoading(true);
            const data = await completionCertificateUpdateAPI(Number(selectedComCerData?.id), certificateTitle, appliedDateDateOnly!, status!, file!);
            setLoading(false);

            if (data.statusCode === 200) {
                onFetchData();
                handleCloseModal();

                if (toast.current) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Success",
                        detail: `Certificate details updated successfully.`,
                        life: 3000,
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error updating certificate.',
                        life: 3000
                    });
                }
            }
        }
    }
    /* ********************************************* Update ************************************** */

    const onStatusChange = (e: any) => {
        setStatus(e.value);
    };

    const handleCloseModal = () => {
        onClose();
        handleClearErrors();
        handleClearFields();
    }


    /************************************* For status ***************************************/
    /* For showing statuses options in the dropdown list */
    const statusOptionTemplate = (option: string) => {
        let colorCode = '';
        if (option === 'Received') {
            colorCode = '#B9F6CA';
        } else if (option === 'In Progress') {
            colorCode = '#FFEFBC';
        } else {
            colorCode = '#EF9A9A';
        }
        return (
            <div className="d-flex align-items-center w-100 justify-content-between">
                <div>{option}</div>
                <div className="color_display" style={{ backgroundColor: colorCode }}></div>
            </div>
        );
    };
    /****************************************************************************************/


    /************************************ For file upload ***********************************/
    const handleFileUpload = (file: File) => {
        setFile(file);
        setFileName(file.name);
    };

    const handleClearFile = () => {
        setFile(null);
        setFileName('');
    };
    /****************************************************************************************/


    /* CompletionCertificateModal Modal header */
    const detailModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState}&nbsp;certificate
            </h5>

            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const detailModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseModal}
            />

            <Button
                label={loading ? 'Processing' : dataState === 'Add' ? 'Save' : 'Update'}
                onClick={dataState === 'Add' ? handleCreateDetails : handleUpdateDetails}
                loading={loading}
                className="custom_btn primary"
                disabled={dataState === 'Edit' && !updateHasChanges}
            />
        </div>
    );
    /*  */

    return (
        <Dialog
            visible={isOpen}
            header={detailModalHeader}
            footer={detailModalFooter}
            headerClassName="custom_modal_header"
            className="custom_modal_dialog modal_dialog_sm"
            onHide={handleCloseModal}
            dismissableMask
        >
            <div className="custom_modal_body">
                <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
                    <div className="row">
                        {/* Certificate title */}
                        <div className="col-12">
                            <TextInput
                                id="certificateTitle"
                                label="Certificate title"
                                labelHtmlFor="certificateTitle"
                                required={true}
                                value={certificateTitle}
                                placeholder="Certificate title"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCertificateTitle(e.target.value)}
                                error={certificateTitleError}
                            />
                        </div>

                        {/* Applied date */}
                        <div className="col-12">
                            <DatePicker
                                id="appliedDate"
                                label="Applied date"
                                labelHtmlFor="appliedDate"
                                required={false}
                                value={appliedDate}
                                minDate={today}
                                placeholder="dd/mm/yyyy"
                                onChange={setAppliedDate}
                                error={appliedDateError}
                                showClear
                                onClear={() => setAppliedDate(null)}
                            />
                        </div>

                        {/* Status */}
                        <div className="col-12">
                            <div className="page_form_group">
                                <label htmlFor="status" className="custom_form_label is_required">Status</label>
                                <Dropdown
                                    id="status"
                                    value={status}
                                    onChange={onStatusChange}
                                    options={statuses}
                                    itemTemplate={statusOptionTemplate}
                                    panelClassName="status_option_panel"
                                    placeholder="Select status"
                                    className="form_dropdown w-100"
                                    showClear
                                    disabled={!isPermission_change_progress}
                                />

                                {statusError &&
                                    <small className="form_error_msg">{statusError}</small>
                                }
                            </div>
                        </div>

                        {/* File */}
                        <div className="col-12">
                            <FileInput
                                id="file"
                                label="Upload file"
                                labelHtmlFor="file"
                                // required={dataState === 'Add' ? true : false}
                                required={false}
                                value={fileName}
                                onUpload={handleFileUpload}
                                onClear={handleClearFile}
                                toast={toast}
                                error={fileError}
                                containerClassName="mb-0"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Dialog >
    )
}

export default CompletionCertificateModal;